import { ShareNetwork, WhatsappLogo, FacebookLogo, InstagramLogo, TiktokLogo, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatGNF } from "@/lib/format";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const SHARE_BASE = "https://bodje-immo.com/api/partage";

export const listingShare = (l) => {
  const place = [l.quartier, l.city].filter(Boolean).join(", ");
  return {
    url: `${SHARE_BASE}/${l.listing_id}`,
    title: l.title,
    text: `${l.title} — ${formatGNF(l.price)}${place ? ` à ${place}` : ""} sur BODJE IMMO GUINÉE`,
  };
};

export const agencyShare = (agent, count) => ({
  url: `${SHARE_BASE}/agence/${agent.id}`,
  title: agent.agency_name || agent.name,
  text: `Découvrez ${agent.agency_name || agent.name} sur BODJE IMMO GUINÉE — ${count} annonce(s) disponible(s)`,
});

function useShareActions({ url, title, text }) {
  const copy = async (hint) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(hint || "Lien copié");
    } catch {
      window.prompt("Copiez le lien :", url);
    }
  };
  const native = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({ title, text, url });
    } catch {}
  };
  const viaApp = (name) => (navigator.share ? native() : copy(`Lien copié — collez-le dans ${name}`));
  const open = (href) => window.open(href, "_blank", "noopener,noreferrer");
  return {
    copy,
    native,
    viaApp,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    open,
  };
}

const roundBtn =
  "btn-pill flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background transition-colors hover:text-white";

export function ShareBar({ share, testPrefix = "share" }) {
  const a = useShareActions(share);
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={`${testPrefix}-section`}>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={a.native}
          data-testid={`${testPrefix}-native-btn`}
          className="btn-pill flex h-10 items-center gap-2 rounded-full bg-secondary px-4 text-sm font-bold text-secondary-foreground hover:bg-secondary/90"
        >
          <ShareNetwork size={18} weight="bold" /> Partager
        </button>
        <a href={a.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" data-testid={`${testPrefix}-whatsapp-btn`} className={`${roundBtn} hover:border-[#25D366] hover:bg-[#25D366]`}>
          <WhatsappLogo size={20} weight="fill" />
        </a>
        <a href={a.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid={`${testPrefix}-facebook-btn`} className={`${roundBtn} hover:border-[#1877F2] hover:bg-[#1877F2]`}>
          <FacebookLogo size={20} weight="fill" />
        </a>
        <button type="button" onClick={() => a.viaApp("Instagram")} aria-label="Instagram" data-testid={`${testPrefix}-instagram-btn`} className={`${roundBtn} hover:border-[#E1306C] hover:bg-[#E1306C]`}>
          <InstagramLogo size={20} weight="fill" />
        </button>
        <button type="button" onClick={() => a.viaApp("TikTok")} aria-label="TikTok" data-testid={`${testPrefix}-tiktok-btn`} className={`${roundBtn} hover:border-black hover:bg-black`}>
          <TiktokLogo size={20} weight="fill" />
        </button>
        <button type="button" onClick={() => a.copy()} aria-label="Copier le lien" data-testid={`${testPrefix}-copy-btn`} className={`${roundBtn} hover:border-primary hover:bg-primary`}>
          <LinkSimple size={20} weight="bold" />
        </button>
      </div>
    </div>
  );
}

export function ShareListing({ listing }) {
  return <ShareBar share={listingShare(listing)} />;
}

export function ShareMenuButton({ share, testid, className = "" }) {
  const a = useShareActions(share);
  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  if (typeof navigator !== "undefined" && navigator.share) {
    return (
      <button type="button" aria-label="Partager" data-testid={testid} onClick={(e) => { stop(e); a.native(); }} className={className}>
        <ShareNetwork size={18} weight="bold" />
      </button>
    );
  }
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild onClick={stop}>
        <button type="button" aria-label="Partager" data-testid={testid} className={className}>
          <ShareNetwork size={18} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={stop} className="w-48">
        <DropdownMenuItem data-testid={`${testid}-whatsapp`} onSelect={() => a.open(a.whatsapp)}>
          <WhatsappLogo size={16} weight="fill" className="mr-2 text-[#25D366]" /> WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testid}-facebook`} onSelect={() => a.open(a.facebook)}>
          <FacebookLogo size={16} weight="fill" className="mr-2 text-[#1877F2]" /> Facebook
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testid}-instagram`} onSelect={() => a.viaApp("Instagram")}>
          <InstagramLogo size={16} weight="fill" className="mr-2 text-[#E1306C]" /> Instagram
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testid}-tiktok`} onSelect={() => a.viaApp("TikTok")}>
          <TiktokLogo size={16} weight="fill" className="mr-2" /> TikTok
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testid}-copy`} onSelect={() => a.copy()}>
          <LinkSimple size={16} weight="bold" className="mr-2" /> Copier le lien
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
